import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowDownToLine,
  ArrowUpFromLine,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Link2Off,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api, type ShopifyCatalogProduct } from "@/lib/api";
import { formatUsd, cn } from "@/lib/utils";

type Filter = "all" | "drifted" | "unmatched" | "synced";

function DriftBadge({ drift, matched }: { drift: ShopifyCatalogProduct["drift"]; matched: boolean }) {
  if (!matched) {
    return (
      <span className="flex items-center gap-1 text-slate-400 text-xs">
        <Link2Off className="h-3.5 w-3.5" />
        no local record
      </span>
    );
  }
  if (drift === "price") {
    return (
      <span className="flex items-center gap-1 text-amber-400 text-xs font-medium">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        price drift
      </span>
    );
  }
  if (drift === "status") {
    return (
      <span className="flex items-center gap-1 text-rose-400 text-xs font-medium">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        status drift
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-emerald-400 text-xs">
      <CheckCircle2 className="h-3.5 w-3.5" />
      in sync
    </span>
  );
}

export function ShopifyCatalogPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<ShopifyCatalogProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pulling, setPulling] = useState<Set<string>>(new Set());
  const [pushing, setPushing] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.shopifyCatalog();
      setProducts(res.products);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load Shopify catalog.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelected(new Set()); }, [filter]);

  const visible = products.filter((p) => {
    if (filter === "drifted") return p.drift !== null && p.local_id !== null;
    if (filter === "unmatched") return p.local_id === null;
    if (filter === "synced") return p.drift === null && p.local_id !== null;
    return true;
  });

  const counts = {
    all: products.length,
    drifted: products.filter((p) => p.drift !== null && p.local_id !== null).length,
    unmatched: products.filter((p) => p.local_id === null).length,
    synced: products.filter((p) => p.drift === null && p.local_id !== null).length,
  };

  const allSelected = visible.length > 0 && visible.every((p) => selected.has(p.shopify_id));
  const someSelected = visible.some((p) => selected.has(p.shopify_id)) && !allSelected;

  const toggleAll = useCallback(() => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) visible.forEach((p) => next.delete(p.shopify_id));
      else visible.forEach((p) => { if (p.local_id) next.add(p.shopify_id); });
      return next;
    });
  }, [allSelected, visible]);

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  // Pull: Shopify → local (updates local record with Shopify's price/qty/images)
  const pullOne = useCallback(async (localId: string, shopifyId: string) => {
    setPulling((s) => new Set(s).add(shopifyId));
    try {
      await api.storePull(localId);
      await load();
    } finally {
      setPulling((s) => { const n = new Set(s); n.delete(shopifyId); return n; });
    }
  }, [load]);

  // Push: local → Shopify (syncs local state/price to Shopify)
  const pushOne = useCallback(async (localId: string, shopifyId: string) => {
    setPushing((s) => new Set(s).add(shopifyId));
    try {
      await api.storeSync(localId);
      await load();
    } finally {
      setPushing((s) => { const n = new Set(s); n.delete(shopifyId); return n; });
    }
  }, [load]);

  const pullSelected = useCallback(async () => {
    const matched = products.filter((p) => selected.has(p.shopify_id) && p.local_id);
    for (const p of matched) await pullOne(p.local_id!, p.shopify_id);
    setSelected(new Set());
  }, [products, selected, pullOne]);

  const pushSelected = useCallback(async () => {
    const matched = products.filter((p) => selected.has(p.shopify_id) && p.local_id);
    for (const p of matched) await pushOne(p.local_id!, p.shopify_id);
    setSelected(new Set());
  }, [products, selected, pushOne]);

  const busy = pulling.size > 0 || pushing.size > 0;
  const selectedMatched = products.filter((p) => selected.has(p.shopify_id) && p.local_id).length;

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => navigate("/store")}
          className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-slate-200 -ml-1"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Store
        </Button>
        <div className="h-4 w-px bg-border" />
        <div className="flex-1">
          <h1 className="text-xl font-semibold tracking-tight">Shopify Catalog</h1>
          <p className="text-muted-foreground text-xs mt-0.5">
            Every product in Shopify — select what to pull locally or push changes.
          </p>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={load}
          disabled={loading}
          className="h-8 gap-1.5 text-xs"
        >
          <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
          Refresh
        </Button>
      </div>

      {/* Filter pills */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {(["all", "drifted", "unmatched", "synced"] as Filter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={cn(
              "px-2.5 py-1 rounded-md text-xs font-medium transition-colors border",
              filter === f
                ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
                : "text-muted-foreground border-border hover:text-slate-200 hover:border-border/80",
            )}
          >
            {f === "all" && `All (${counts.all})`}
            {f === "drifted" && (
              <span className="flex items-center gap-1">
                {counts.drifted > 0 && <AlertTriangle className="h-3 w-3 text-amber-400" />}
                {`Drifted (${counts.drifted})`}
              </span>
            )}
            {f === "unmatched" && `No local record (${counts.unmatched})`}
            {f === "synced" && `In sync (${counts.synced})`}
          </button>
        ))}
      </div>

      {/* Batch toolbar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-500/20 bg-slate-900/80 px-3 py-2">
          <span className="text-xs font-medium text-emerald-400 tabular-nums">
            {selected.size} selected
            {selectedMatched < selected.size && (
              <span className="text-muted-foreground ml-1">
                ({selected.size - selectedMatched} unmatched — no local record)
              </span>
            )}
          </span>
          <Button
            size="sm"
            disabled={busy || selectedMatched === 0}
            onClick={pullSelected}
            className="h-7 gap-1.5 text-xs bg-slate-500/20 border border-slate-500/40 text-slate-300 hover:bg-slate-500/30"
          >
            <ArrowDownToLine className="h-3 w-3" />
            ← Pull from Shopify ({selectedMatched})
          </Button>
          <Button
            size="sm"
            disabled={busy || selectedMatched === 0}
            onClick={pushSelected}
            className="h-7 gap-1.5 text-xs bg-amber-500/20 border border-amber-500/40 text-amber-300 hover:bg-amber-500/30"
          >
            <ArrowUpFromLine className="h-3 w-3" />
            → Push to Shopify ({selectedMatched})
          </Button>
          <button
            onClick={() => setSelected(new Set())}
            className="ml-auto text-xs text-muted-foreground hover:text-slate-200"
          >
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div className={cn("rounded-lg border border-border/60 overflow-hidden transition-opacity", loading && "opacity-60")}>
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              <TableHead className="w-8 pl-3 pr-1">
                <Checkbox
                  checked={allSelected || (someSelected ? "indeterminate" : false)}
                  onCheckedChange={toggleAll}
                  aria-label="Select all"
                  disabled={loading}
                />
              </TableHead>
              <TableHead className="w-full min-w-0">
                <span className="uppercase text-[10px] font-medium tracking-wide">Product</span>
              </TableHead>
              <TableHead className="w-28 whitespace-nowrap">
                <span className="uppercase text-[10px] font-medium tracking-wide">Shopify</span>
              </TableHead>
              <TableHead className="w-28 whitespace-nowrap tabular-nums">
                <span className="uppercase text-[10px] font-medium tracking-wide">Shopify Price</span>
              </TableHead>
              <TableHead className="w-28 whitespace-nowrap tabular-nums">
                <span className="uppercase text-[10px] font-medium tracking-wide">Local Price</span>
              </TableHead>
              <TableHead className="w-32 whitespace-nowrap">
                <span className="uppercase text-[10px] font-medium tracking-wide">Status</span>
              </TableHead>
              <TableHead className="w-28 whitespace-nowrap">
                <span className="uppercase text-[10px] font-medium tracking-wide">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 7 }).map((_, j) => (
                    <TableCell key={j}><div className="h-4 rounded shimmer" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : error ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-destructive py-12 text-sm">
                  {error}
                </TableCell>
              </TableRow>
            ) : visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-12 text-sm">
                  No products in this view.
                </TableCell>
              </TableRow>
            ) : (
              visible.map((p) => {
                const isMatched = p.local_id !== null;
                const isPulling = pulling.has(p.shopify_id);
                const isPushing = pushing.has(p.shopify_id);
                const isSelected = selected.has(p.shopify_id);

                return (
                  <TableRow
                    key={p.shopify_id}
                    className={cn(
                      "hover:bg-muted/20",
                      isSelected && "bg-emerald-500/5 hover:bg-emerald-500/8",
                    )}
                  >
                    <TableCell className="py-2.5 pl-3 pr-1 w-8">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggle(p.shopify_id)}
                        aria-label={`Select ${p.title}`}
                      />
                    </TableCell>

                    <TableCell className="py-2.5 w-full min-w-0 max-w-0">
                      <div className="font-medium text-sm leading-tight truncate" title={p.title}>
                        {p.title}
                      </div>
                      {isMatched && p.local_title && p.local_title !== p.title && (
                        <div className="text-muted-foreground/60 text-[10px] mt-0.5 truncate" title={p.local_title}>
                          local: {p.local_title}
                        </div>
                      )}
                      <div className="text-muted-foreground/50 font-mono text-[10px] mt-0.5">
                        #{p.shopify_id}
                      </div>
                    </TableCell>

                    <TableCell className="py-2.5 whitespace-nowrap">
                      <Badge
                        className={cn(
                          "text-[10px] py-0 px-1.5",
                          p.shopify_status === "ACTIVE" && "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
                          p.shopify_status === "DRAFT" && "bg-slate-500/20 text-slate-400 border-slate-500/30",
                          p.shopify_status === "ARCHIVED" && "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
                        )}
                      >
                        {p.shopify_status?.charAt(0) + p.shopify_status?.slice(1).toLowerCase()}
                      </Badge>
                    </TableCell>

                    <TableCell className="py-2.5 whitespace-nowrap font-mono tabular-nums text-xs">
                      {p.shopify_price ? formatUsd(parseFloat(p.shopify_price)) : "—"}
                    </TableCell>

                    <TableCell className="py-2.5 whitespace-nowrap font-mono tabular-nums text-xs">
                      {p.local_price
                        ? <span className={cn(p.drift === "price" && "text-amber-400")}>
                            {formatUsd(parseFloat(p.local_price))}
                          </span>
                        : <span className="text-muted-foreground/40">—</span>
                      }
                    </TableCell>

                    <TableCell className="py-2.5 whitespace-nowrap">
                      <DriftBadge drift={p.drift} matched={isMatched} />
                    </TableCell>

                    <TableCell className="py-2.5 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        {isMatched ? (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => pullOne(p.local_id!, p.shopify_id)}
                              disabled={isPulling || isPushing}
                              title="← Pull from Shopify (update local with Shopify data)"
                              className="h-6 w-6 p-0 text-slate-400 hover:text-slate-200"
                            >
                              <ArrowDownToLine className={cn("h-3 w-3", isPulling && "animate-bounce")} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => pushOne(p.local_id!, p.shopify_id)}
                              disabled={isPulling || isPushing}
                              title="→ Push to Shopify (sync local changes to Shopify)"
                              className={cn(
                                "h-6 w-6 p-0 transition-colors",
                                p.drift
                                  ? "text-amber-400 hover:text-amber-300"
                                  : "text-muted-foreground/40 hover:text-slate-300",
                              )}
                            >
                              <ArrowUpFromLine className={cn("h-3 w-3", isPushing && "animate-bounce")} />
                            </Button>
                            <button
                              onClick={() => navigate(`/curated/${p.local_id}`)}
                              className="text-[10px] text-muted-foreground/50 hover:text-emerald-400 transition-colors ml-0.5"
                            >
                              edit
                            </button>
                          </>
                        ) : (
                          <span className="text-[10px] text-muted-foreground/40 italic">
                            use Import
                          </span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {!loading && !error && (
        <div className="text-xs text-muted-foreground tabular-nums">
          {visible.length} of {products.length} Shopify products
          {selected.size > 0 && <span className="ml-2 text-emerald-400">· {selected.size} selected</span>}
        </div>
      )}
    </div>
  );
}
